<template>
  <!-- 他のAI機能への切替パネル。開いている間は入力欄と入れ替わる。
       上端中央の−ボタンで閉じる -->
  <div class="service-menu">
    <button
      ref="closeButton"
      type="button"
      class="service-menu__close"
      aria-label="メニューを閉じる"
      @click="$emit('close')"
    >
      <svg
        viewBox="0 0 24 24"
        width="20"
        height="20"
        fill="none"
        stroke="currentColor"
        stroke-width="2.4"
        stroke-linecap="round"
        aria-hidden="true"
      >
        <path d="M6 12h12" />
      </svg>
    </button>

    <ul class="service-menu__list">
      <li v-for="item in items" :key="item.key">
        <button
          type="button"
          class="service-menu__item"
          @click="$emit('select', item)"
        >
          <svg
            class="service-menu__icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <template v-if="item.key === 'concierge'">
              <circle cx="12" cy="12" r="9.2" />
              <path
                d="M9.4 9.3a2.6 2.6 0 1 1 3.4 2.5c-.7.2-1.1.8-1.1 1.5v.4M12 17.2h.01"
              />
            </template>
            <template v-else-if="item.key === 'resume-chat'">
              <path d="M16.6 3.6a2.1 2.1 0 0 1 3 3L8.4 17.8 4 19.4l1.6-4.4z" />
              <path d="M14.6 5.6l3.8 3.8" />
            </template>
            <path
              v-else-if="item.key === 'interview'"
              d="M20.5 11.6a8 8 0 0 1-8.5 8 8.3 8.3 0 0 1-3.6-.8L4 20.4l1.6-4.3a8 8 0 0 1-.9-3.7 8 8 0 0 1 8-8 8 8 0 0 1 7.8 7.2z"
            />
            <template v-else-if="item.key === 'aptitude'">
              <rect x="4" y="4" width="16" height="16" rx="2.2" />
              <path d="M4 8.6h16M13 8.6v4.2l2.2-1.4 2.2 1.4V8.6" />
            </template>
            <template v-else>
              <path d="M4 4v16h16" />
              <path d="M7 15.4l3.4-4.2 3 2.4 4.2-5.4" />
            </template>
          </svg>

          <span class="service-menu__text">
            <span class="service-menu__label">{{ item.label }}</span>
            <span class="service-menu__description">{{
              item.description
            }}</span>
          </span>
        </button>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

type ServiceMenuItem = {
  key: string
  label: string
  description: string
  route: string
}

defineProps({
  // メニュー項目。文言・遷移先の正本は utils/ai/careerChatMenu.json
  items: { type: Array as () => ServiceMenuItem[], default: () => [] }
})
defineEmits(['select', 'close'])

const closeButton = ref<HTMLButtonElement | null>(null)

// 開いた直後にフォーカスをパネル内へ移すため、親から呼ぶ。
// ＋ボタンは v-if で消えるので、移さないとフォーカスが body へ落ちて
// キーボードではメニューに到達できなくなる。**見た目のリングは
// :focus-visible 側で抑える**（マウスで開いたときに枠を出さない）
const focusClose = (): void => {
  closeButton.value?.focus()
}

defineExpose({ focusClose })
</script>

<style lang="scss" scoped>
.service-menu {
  position: relative;

  /* 上に余白を作らない。−ボタンは会話エリア側へはみ出させる
     （margin-top を入れると白帯が残る） */
  margin: 0 8px 8px;
  padding: 16px 10px 10px;
  border: 2px solid #3f9e5f;
  border-radius: 16px;
  background: #eef6f0;
}

.service-menu__close {
  position: absolute;
  top: -19px;
  left: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  padding: 0;
  transform: translateX(-50%);
  border: 2px solid #3f9e5f;
  border-radius: 50%;
  background: #fff;
  color: #3f9e5f;
  cursor: pointer;
}

/* 汎用の hover で透けてしまうため、不透明を明示する */
.service-menu__close:hover,
.service-menu__close:focus {
  background: #fff;
  color: #3f9e5f;
  opacity: 1;
}

/* マウスで開いたときに枠を出さない。キーボード操作では残す */
.service-menu__close:focus:not(:focus-visible) {
  outline: none;
}

.service-menu__list {
  max-height: min(52vh, 380px);
  margin: 0;
  padding: 0;
  overflow-y: auto;
  list-style: none;
}

.service-menu__item {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 9px 12px;
  border: none;
  border-radius: 10px;
  background: none;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s;
}

.service-menu__item:hover {
  background: #e2f0e7;
}

.service-menu__icon {
  flex: 0 0 auto;
  width: 26px;
  height: 26px;
  color: #3f9e5f;
}

.service-menu__text {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.service-menu__label {
  color: #2f8b4f;
  font-size: 15px;
  font-weight: bold;
  line-height: 1.3;
}

.service-menu__description {
  color: #8a9a92;
  font-size: 12px;
  line-height: 1.4;
}
</style>
